import React, { useEffect, useRef, useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import {
  FLOW,
  PROBLEM_KIND,
  deletedAfter,
  deletesOn,
  noticeClearsOn,
  movedTo,
  planLabel,
  pubLabel,
} from "./flowCopy";
import { pageUnit } from "./PlanCard";
import { pendingRejection } from "./planFlow";
import { shortDate } from "./shared";
import type {
  AccountPlanStatus,
  PlanActivityRow,
  PlanFile,
  PlanRejectedUpload,
  ReviewSummary,
  SectionOwner,
} from "../../Types/AccountPlan";

// ACT-02 / F-UF-E18 option (b), Sushant 2026-10-30: the history lists every
// confirmed version and the rejected uploads. A withdrawn upload was never a
// version (D1) and has no file, so it has no row here; the Activity log keeps it.
// The old per-version journey (journeyOf) is gone (F-UF-F25).

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// ---------------------------------------------------------------------------
// Versions vs rejected uploads (upload-flow Phase 1)
// ---------------------------------------------------------------------------

/** A usable version: read, kept, and able to carry a briefing. */
export const isVersion = (f: PlanFile) =>
  f.status === "ready" || f.status === "partial";

/** An upload that stopped before becoming a version: none reached the sponsor. */
export const isRejectedUpload = (f: PlanFile) =>
  f.status === "failed" ||
  f.status === "rejected" ||
  f.status === "needs_review";

/** The newest problem shape (`AccountPlanStatus.latest_file.problem`). */
export type UploadProblem = NonNullable<
  NonNullable<AccountPlanStatus["latest_file"]>["problem"]
>;

export interface Partition {
  /** Usable versions, newest first. */
  versions: PlanFile[];
  /** Rejected uploads, newest first. */
  rejected: PlanFile[];
  /** The newest version sponsors read as published (null: nothing published). */
  published: PlanFile | null;
  /** The newest upload of all, when it is a rejected one: the problem to fix. */
  problem: PlanFile | null;
}

/**
 * Splits an account's files into the versions (ready/partial) and the uploads that
 * never became one. Files still being read are neither yet. The published version is
 * the newest version with a publish record; older published ones were replaced.
 */
export const partitionFiles = (files: PlanFile[]): Partition => {
  const sorted = [...files].sort((a, b) => b.version - a.version);
  const versions = sorted.filter(isVersion);
  const rejected = sorted.filter(isRejectedUpload);
  // A discarded upload ("Not this file") is listed but is never the problem to fix:
  // the account reads as it did before it (Phase 2).
  const newest = sorted.filter(f => !f.discarded)[0];
  return {
    versions,
    rejected,
    published: versions.find(f => !!f.published) || null,
    problem: newest && isRejectedUpload(newest) ? newest : null,
  };
};

/**
 * "Sponsor has v2 (published Oct 27) · 2 versions · 2 rejected uploads". The
 * rejected count covers both kinds: uploads that stopped after reading and
 * pre-parse rejections (Phase 3, no file).
 */
export const historySummary = (
  files: PlanFile[],
  rejections: PlanRejectedUpload[] = []
): string => {
  const p = partitionFiles(files);
  // The current upload is shown on its own above the history, not as "not used".
  const nRejected =
    p.rejected.filter(f => f.id !== p.problem?.id || !!f.discarded).length +
    rejections.length;
  const head = p.published?.published
    ? `Sponsor has ${p.published.plan_version != null ? `v${p.published.plan_version}` : pubLabel(p.published.published)} (published ${shortDate(p.published.published.at)})`
    : "Nothing published yet";
  // F-UF-F26: a version is numbered once confirmed (D1); a read upload still
  // waiting on Confirm plan is counted apart, not as a version.
  const waiting = p.versions.filter(f => f.confirmed === false).length;
  const kept = p.versions.length - waiting;
  return [
    // Nothing yet reads as one phrase, not "Nothing published yet · 0 versions".
    !kept && !p.published?.published ? "No versions yet" : head,
    kept ? plural(kept, "version") : null,
    waiting ? `${waiting} waiting to confirm` : null,
    nRejected ? `${plural(nRejected, "upload")} not used` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

const ERROR_CHIPS: [string, string][] = [
  ["scanned", "Scanned PDF"],
  ["password", "Password-protected"],
  ["damaged", "Damaged file"],
  ["too many", "Too many pages"],
  ["empty", "Empty file"],
  ["interrupted", "Interrupted"],
];

/**
 * The short, plain reason an upload stopped (mirrors backend activity.reason_of):
 * the panel's `latest_file.problem` or the Activity row win when they are known.
 */
export const reasonOf = (f: PlanFile, row?: PlanActivityRow): string => {
  if (f.discarded?.moved_to) return movedTo(f.discarded.moved_to_name);
  if (f.discarded) return FLOW.youDiscarded;
  // Deleted when a newer upload became the plan (D23, failure audit G13).
  if (f.validation?.replaced_by) return FLOW.replacedByNewer;
  // Its stored file is gone (D3): that is what matters now (backend reason_of "expired").
  if (f.purged) return deletedAfter(f);
  if (row?.reason) return row.reason;
  const checks = f.validation?.checks || [];
  if (f.status === "failed") {
    const t = (f.error || "").toLowerCase();
    return ERROR_CHIPS.find(([k]) => t.includes(k))?.[1] || "Couldn't read";
  }
  if (f.status === "rejected") {
    const c = checks.find(x => x.status === "fail" && x.blocking);
    return c?.problem || c?.title || "A plan check failed";
  }
  const n = checks.filter(x => x.status === "warn").length;
  return n ? `${plural(n, "thing")} to check` : "Waiting for review";
};

/** The full sentence for a rejected upload. */
export const detailOf = (f: PlanFile): string | null => {
  if (f.error) return f.error;
  const c = (f.validation?.checks || []).find(
    x => x.status === "fail" || x.status === "warn"
  );
  return c?.detail || null;
};

// ---------------------------------------------------------------------------
// A version's status, in one chip and one key fact
// ---------------------------------------------------------------------------

type VersionStatus =
  | "published"
  | "replaced"
  | "review"
  | "draft"
  | "confirm"
  | "setaside";

const CHIP: Record<VersionStatus, { label: string; cls: string }> = {
  published: { label: "Published", cls: "bg-emerald-50 text-emerald-700" },
  replaced: { label: "Replaced", cls: "bg-gray-100 text-gray-600" },
  review: { label: "In review", cls: "bg-purple-50 text-[#7E22CE]" },
  draft: { label: "Draft", cls: "bg-blue-50 text-blue-700" },
  // Read, waiting on the AE's Confirm plan (Phase 2).
  confirm: { label: FLOW.confirmPlan, cls: "bg-amber-50 text-amber-800" },
  setaside: { label: "Set aside", cls: "bg-gray-100 text-gray-500" },
};

const ROLES: SectionOwner[] = ["AE", "CSM"];

const verifiedOf = (r?: ReviewSummary | null) => {
  if (!r?.progress) return null;
  const owned = ROLES.reduce((n, k) => n + (r.progress[k]?.owned || 0), 0);
  const verified = ROLES.reduce(
    (n, k) => n + (r.progress[k]?.verified || 0),
    0
  );
  return owned ? { owned, verified } : null;
};

interface VersionInfo {
  status: VersionStatus;
  key: string;
  /** When a newer version was published over this one. */
  replacedAt: string | null;
}

const versionInfo = (
  f: PlanFile,
  p: Partition,
  currentFileId: string | null,
  review?: ReviewSummary | null
): VersionInfo => {
  if (f.published) {
    if (p.published?.id === f.id)
      return {
        status: "published",
        key: "Sponsor reads this",
        replacedAt: null,
      };
    // The next newer published version replaced it.
    const newer = p.versions
      .filter(v => v.version > f.version && v.published)
      .sort((a, b) => a.version - b.version)[0];
    const at = newer?.published?.at || null;
    return {
      status: "replaced",
      key: at ? `Replaced on ${shortDate(at)}` : "Replaced",
      replacedAt: at,
    };
  }
  if (f.id !== currentFileId)
    return {
      status: "setaside",
      key: "Set aside by a newer upload",
      replacedAt: null,
    };
  if (f.review_status === "draft" || f.review_status === "in_review") {
    const v = verifiedOf(review);
    return {
      status: "review",
      key: v ? `${v.verified} of ${v.owned} verified` : "Draft briefing ready",
      replacedAt: null,
    };
  }
  if (f.confirmed === false)
    return {
      status: "confirm",
      key: FLOW.waitingToConfirm,
      replacedAt: null,
    };
  return {
    status: "draft",
    key:
      f.sponsor_view_status === "processing"
        ? "Writing the briefing"
        : f.sponsor_view_status === "failed"
          ? "The briefing didn't finish"
          : "No briefing yet",
    replacedAt: null,
  };
};

const Chev: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
  ) : (
    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
  );

const SMALL_BTN =
  "inline-flex h-8 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

const Col: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="min-w-0 space-y-0.5">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {title}
    </h4>
    {children}
  </div>
);

interface Props {
  files: PlanFile[];
  currentFileId: string | null;
  /** Kept for callers; the published version is derived from the files. */
  sponsorFileId?: string | null;
  /** Activity rows by file id, read only for the plain reason an upload stopped. */
  journeys?: Record<string, PlanActivityRow>;
  /** The newest upload's problem from the Plans list (wins for that file). */
  latestProblem?: { fileId: string; problem: UploadProblem } | null;
  /** Review summaries the caller has (current, published…), matched by file id. */
  reviews?: (ReviewSummary | null | undefined)[];
  /** Panel: no dates, people or buttons in the rows. */
  compact?: boolean;
  /** Whether the whole section starts open. */
  defaultOpen?: boolean;
  /** Plan page deep link (`focus=problem`): open and outline the newest problem. */
  focusProblem?: boolean;
  onOpenViewer?: (fileId: string) => void;
  onOpenPublished?: () => void;
  onOpenDraft?: () => void;
  /** Actions for the newest upload when it is a rejected one (plan page only). */
  problemActions?: (f: PlanFile) => React.ReactNode;
  /** Actions on a discarded upload ("Undo" while it can be restored; plan page only). */
  discardActions?: (f: PlanFile) => React.ReactNode;
  /** Pre-parse rejections (Phase 3, overview `rejected_uploads`): no file, merged in. */
  rejections?: PlanRejectedUpload[];
  /** Actions on the outstanding pre-parse rejection ("Dismiss"; plan page, AE only). */
  rejectionActions?: (r: PlanRejectedUpload) => React.ReactNode;
  /**
   * Whether the outstanding pre-parse rejection is this viewer's problem (the AE):
   * for anyone else it is listed but never pointed at, as their status ignores it.
   */
  rejectionIsProblem?: boolean;
  /** The viewer is the one who confirms uploads (the AE): "once you confirm it". */
  viewerConfirms?: boolean;
  /**
   * "Delete" on any upload that didn't become a plan (2026-10-31; plan page, AE
   * only): a refused upload's notice, or a stored upload not yet deleted.
   */
  unusedActions?: (u: UnusedUpload) => React.ReactNode;
  /** The upload's next step ("Review"), on the row's own line, open or not. */
  problemPrimary?: (f: PlanFile) => React.ReactNode;
}

/** An upload in the not-used group that can still be deleted. */
export type UnusedUpload =
  | { kind: "file"; f: PlanFile }
  | { kind: "pre"; r: PlanRejectedUpload };

/** One row of the Rejected uploads group: a stopped upload, or a pre-parse record. */
type RejectedItem =
  | { kind: "file"; key: string; at: string; f: PlanFile }
  | { kind: "pre"; key: string; at: string; r: PlanRejectedUpload };

const preKey = (r: PlanRejectedUpload, i: number) =>
  `pre:${r.at || ""}:${r.filename || ""}:${i}`;

/**
 * Version history as an accordion (upload-flow Phase 1): the usable versions, each a
 * row with one chip and one key fact, then the uploads that never became a version,
 * grouped and named by their plain reason. Shared by the plan page and the panel.
 */
const VersionHistory: React.FC<Props> = ({
  files,
  currentFileId,
  journeys = {},
  latestProblem,
  reviews = [],
  compact = false,
  defaultOpen = true,
  focusProblem = false,
  onOpenViewer,
  onOpenPublished,
  onOpenDraft,
  problemActions,
  discardActions,
  rejections = [],
  rejectionActions,
  rejectionIsProblem = true,
  viewerConfirms = false,
  unusedActions,
  problemPrimary,
}) => {
  const p = partitionFiles(files);
  // The outstanding pre-parse rejection is newer than any upload, so when there is
  // one it is the problem to point at; otherwise the newest stopped upload is.
  const pre = rejectionIsProblem ? pendingRejection(files, rejections) : null;
  const items: RejectedItem[] = [
    ...p.rejected.map(
      (f): RejectedItem => ({
        kind: "file",
        key: f.id,
        at: f.created_at || "",
        f,
      })
    ),
    ...rejections.map(
      (r, i): RejectedItem => ({
        kind: "pre",
        key: preKey(r, i),
        at: r.at || "",
        r,
      })
    ),
  ].sort((a, b) => b.at.localeCompare(a.at));
  const preItem = pre
    ? items.find(it => it.kind === "pre" && it.r === pre) || null
    : null;
  const targetKey = preItem ? preItem.key : p.problem?.id || null;
  const focus = focusProblem && !!targetKey;
  const [sectionOpen, setSectionOpen] = useState(defaultOpen || focus);
  const [groupOpen, setGroupOpen] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Record<string, boolean>>({});
  const problemRef = useRef<HTMLLIElement>(null);
  const newestVersion = p.versions[0]?.id;
  const autoProblem = !!targetKey && !compact;
  // F-UF-F19: right after "Not this file", its Undo must be in view, not inside a
  // collapsed group: the newest upload of all, discarded and still undoable.
  const newestUpload = [...files].sort((a, b) => b.version - a.version)[0];
  const undoable =
    !compact &&
    !!discardActions &&
    !!newestUpload?.discarded &&
    !newestUpload.discarded.moved_to &&
    !newestUpload.purged &&
    !!newestUpload.discarded.undo_until &&
    Date.now() < new Date(newestUpload.discarded.undo_until).getTime();
  const isGroupOpen = groupOpen ?? (focus || autoProblem || undoable);
  const isRowOpen = (id: string) =>
    rows[id] ??
    (id === newestVersion || (id === targetKey && (focus || autoProblem)));
  const toggle = (id: string) => setRows(r => ({ ...r, [id]: !isRowOpen(id) }));

  useEffect(() => {
    if (!focus) return;
    setSectionOpen(true);
    setGroupOpen(true);
    if (targetKey) setRows(r => ({ ...r, [targetKey]: true }));
    const el = problemRef.current;
    if (el && typeof el.scrollIntoView === "function")
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Only when the deep link (or the problem it points at) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, targetKey]);

  const reviewOf = (id: string) => reviews.find(r => r?.file_id === id);

  const renderItem = (item: RejectedItem) => {
    if (item.kind === "pre") {
      const r = item.r;
      const isTarget = item.key === targetKey;
      const outlined = isTarget && focus;
      const open = isRowOpen(item.key);
      const reason = r.reason || "Rejected before reading";
      return (
        <li
          key={item.key}
          ref={isTarget ? problemRef : undefined}
          data-testid="rejected-upload"
          data-stage="pre-parse"
          data-problem={isTarget || undefined}
          data-focused={outlined || undefined}
          className={`rounded-lg border ${outlined ? "border-red-500 ring-2 ring-red-200" : "border-gray-200"}`}
        >
          <button
            type="button"
            onClick={() => toggle(item.key)}
            aria-expanded={open}
            aria-label={`${reason}: ${open ? "hide" : "show"} details`}
            className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            <Chev open={open} />
            <span className="min-w-0 flex-1 truncate text-[13px]">
              <span className="font-medium text-gray-900">{reason}</span>
              {r.filename && (
                <span className="text-gray-500"> · {r.filename}</span>
              )}
            </span>
            {outlined && (
              <span className="whitespace-nowrap rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                The problem
              </span>
            )}
            {!compact && (
              <span className="whitespace-nowrap text-xs text-gray-500">
                {shortDate(r.at)}
              </span>
            )}
            <span className="whitespace-nowrap rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">
              {FLOW.rejected}
            </span>
          </button>
          {open &&
            !compact &&
            ((isTarget && rejectionActions) || (r.id && unusedActions)) && (
              <div className="flex flex-wrap gap-2 px-3 pb-2.5 pl-9">
                {isTarget && rejectionActions?.(r)}
                {r.id && unusedActions?.({ kind: "pre", r })}
              </div>
            )}
          {open && (
            <div className="space-y-2 px-3 pb-3 pl-9">
              {r.message && (
                <p className="text-[13px] text-gray-700">{r.message}</p>
              )}
              <p className="text-xs text-gray-500">
                {FLOW.fileWasntKept}
                {!compact && r.by_name ? ` · ${r.by_name}` : ""}
                {r.dismissed ? " · Dismissed" : ""}
              </p>
              {r.clears_at && (
                <p
                  className="text-xs text-gray-500"
                  data-testid="rejection-clears"
                >
                  {noticeClearsOn(shortDate(r.clears_at))}
                </p>
              )}
            </div>
          )}
        </li>
      );
    }
    const f = item.f;
    const isProblem = f.id === p.problem?.id;
    const isTarget = f.id === targetKey;
    const fromList =
      latestProblem?.fileId === f.id ? latestProblem.problem : null;
    // F-UF-E21: a file "Move it here" took away was never a problem with
    // the upload: where it went, no Post-parse stage, no Undo.
    const moved = !!f.discarded?.moved_to;
    const reason = moved
      ? reasonOf(f)
      : fromList?.reason || reasonOf(f, journeys[f.id]);
    const detail = moved ? null : fromList?.detail || detailOf(f);
    const open = isRowOpen(f.id);
    const outlined = isTarget && focus;
    // Still stored and waiting on the AE (2026-10-31): one with a problem
    // is also deleted on expires_at if the AE does nothing.
    const deletable = !f.purged && (f.deletable_unused ?? !!f.expires_at);
    const kind = moved
      ? FLOW.moved
      : f.discarded
        ? FLOW.discarded
        : fromList
          ? PROBLEM_KIND[fromList.state]
          : PROBLEM_KIND.needs_fixing;
    const extra =
      f.discarded && !moved && !compact && discardActions
        ? discardActions(f)
        : null;
    return (
      <li
        key={f.id}
        ref={isTarget ? problemRef : undefined}
        data-testid="rejected-upload"
        data-stage="post-parse"
        data-problem={isProblem || undefined}
        data-focused={outlined || undefined}
        className={`rounded-lg border ${outlined ? "border-red-500 ring-2 ring-red-200" : "border-gray-200"}`}
      >
        <div className={`flex items-center gap-2 ${extra ? "pr-3" : ""}`}>
          <button
            type="button"
            onClick={() => toggle(f.id)}
            aria-expanded={open}
            aria-label={`${reason}: ${open ? "hide" : "show"} details`}
            className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            <Chev open={open} />
            <span className="min-w-0 flex-1 truncate text-[13px]">
              <span className="font-medium text-gray-900">{reason}</span>
              <span className="text-gray-500"> · {f.filename}</span>
            </span>
            {outlined && (
              <span className="whitespace-nowrap rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                The problem
              </span>
            )}
            {!compact && (
              <span className="whitespace-nowrap text-xs text-gray-500">
                {shortDate(f.created_at)}
              </span>
            )}
            <span
              className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                f.discarded
                  ? "bg-gray-100 text-gray-600"
                  : "bg-amber-50 text-amber-800"
              }`}
            >
              {kind}
            </span>
          </button>
          {!compact && isProblem && problemPrimary?.(f)}
          {extra}
        </div>
        {open &&
          !compact &&
          ((isProblem && problemActions) || (deletable && unusedActions)) && (
            <div className="flex flex-wrap gap-2 px-3 pb-2.5 pl-9">
              {isProblem && problemActions?.(f)}
              {deletable && unusedActions?.({ kind: "file", f })}
            </div>
          )}
        {open && (
          <div className="space-y-2 px-3 pb-3 pl-9">
            {detail && <p className="text-[13px] text-gray-700">{detail}</p>}
            {f.purged && (
              <p className="text-xs text-gray-500">{deletedAfter(f)}</p>
            )}
            {!f.purged && f.expires_at && (
              <p className="text-xs text-gray-500" data-testid="upload-expires">
                {deletesOn(shortDate(f.expires_at))}
              </p>
            )}
          </div>
        )}
      </li>
    );
  };

  // The upload the AE is working on now is not "history": it sits above Version
  // history, open, with its actions in reach (Sushant, 2026-11-02).
  const currentItem = targetKey
    ? items.find(it => it.key === targetKey) || null
    : null;
  const earlier = items.filter(it => it !== currentItem);

  return (
    <div className="space-y-3">
      {currentItem && (
        <section
          aria-label={FLOW.currentUpload}
          data-testid="current-upload"
          className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div className="px-4 pb-1 pt-3 text-sm font-semibold text-gray-900">
            {FLOW.currentUpload}
          </div>
          <ol aria-label={FLOW.currentUpload} className="px-4 pb-3">
            {renderItem(currentItem)}
          </ol>
        </section>
      )}
      {!!earlier.length && (
        <section
          aria-label={FLOW.notUsedUploads}
          data-testid="rejected-uploads"
          className="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <button
            type="button"
            onClick={() => setGroupOpen(!isGroupOpen)}
            aria-expanded={isGroupOpen}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          >
            <Chev open={isGroupOpen} />
            <span className="text-sm font-semibold text-gray-900">
              {FLOW.notUsedUploads} ({earlier.length})
            </span>
          </button>
          <ol
            hidden={!isGroupOpen}
            aria-label={FLOW.notUsedUploads}
            className="space-y-2 px-4 pb-3"
          >
            {earlier.map(renderItem)}
          </ol>
        </section>
      )}
      <section
        aria-label={FLOW.versionHistory}
        data-testid="version-history"
        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
      >
        <button
          type="button"
          onClick={() => setSectionOpen(o => !o)}
          aria-expanded={sectionOpen}
          className="flex w-full items-center gap-2 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <Chev open={sectionOpen} />
          <span className="text-sm font-semibold text-gray-900">
            {FLOW.versionHistory}
          </span>
          <span
            data-testid="history-summary"
            className="min-w-0 truncate text-xs text-gray-500"
          >
            {historySummary(files, rejections)}
          </span>
        </button>

        <div hidden={!sectionOpen} className="border-t border-gray-100">
          {!p.versions.length ? (
            <p className="py-3 pl-10 pr-4 text-sm text-gray-500">
              {/* D1, F-UF-E13: a version is a confirmed upload. */}
              {viewerConfirms
                ? "No versions yet. An upload becomes a version once you confirm it."
                : "No versions yet. An upload becomes a version once the AE confirms it."}
            </p>
          ) : null}
          {!!p.versions.length && (
            <ol aria-label="Versions" className="divide-y divide-gray-100">
              {p.versions.map(f => {
                const info = versionInfo(f, p, currentFileId, reviewOf(f.id));
                const open = isRowOpen(f.id);
                const chip = CHIP[info.status];
                const review = reviewOf(f.id);
                const v = verifiedOf(review);
                const live = info.status === "published";
                const draftable =
                  f.id === currentFileId &&
                  (info.status === "review" ||
                    (info.status === "draft" && !!f.sponsor_view_status));
                return (
                  <li
                    key={f.id}
                    data-testid="history-version"
                    data-version={f.version}
                    data-status={info.status}
                  >
                    {/* One level in from the section header: the chevron sits under
                        the "Version history" label (2026-11-02). */}
                    <div className="flex items-center gap-2 py-2.5 pl-10 pr-4">
                      <button
                        type="button"
                        onClick={() => toggle(f.id)}
                        aria-expanded={open}
                        aria-label={`${planLabel(f)}: ${open ? "hide" : "show"} details`}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                      >
                        <Chev open={open} />
                        <span className="text-sm font-semibold text-gray-900">
                          {planLabel(f)}
                        </span>
                        <span
                          className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${chip.cls}`}
                        >
                          {chip.label}
                        </span>
                        <span className="min-w-0 truncate text-[13px] text-gray-700">
                          {info.key}
                        </span>
                        {!compact && (
                          <span className="ml-auto whitespace-nowrap text-xs text-gray-500">
                            {[
                              shortDate(f.published?.at || f.created_at),
                              f.published?.by_name || f.uploaded_by_name,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </button>
                      {!compact && live && onOpenPublished && (
                        <button
                          type="button"
                          onClick={onOpenPublished}
                          className={SMALL_BTN}
                        >
                          {FLOW.openPublished}
                        </button>
                      )}
                    </div>
                    {open && (
                      <div className="pb-3 pl-16 pr-4">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <Col title="Source">
                            <p
                              className="truncate text-[13px] text-gray-800"
                              title={f.filename}
                            >
                              {f.filename}
                            </p>
                            {!!f.page_count && (
                              <p className="text-xs text-gray-500">
                                {f.page_count} {pageUnit(f, f.page_count)}
                              </p>
                            )}
                          </Col>
                          <Col title="Briefing">
                            <p className="text-[13px] text-gray-800">
                              {f.published
                                ? `Published ${shortDate(f.published.at)}${f.published.by_name ? ` by ${f.published.by_name}` : ""}`
                                : info.key}
                            </p>
                            {review?.progress && v && (
                              <p className="text-xs text-gray-500">
                                Verified: AE {review.progress.AE?.verified ?? 0}
                                /{review.progress.AE?.owned ?? 0} · CSM{" "}
                                {review.progress.CSM?.verified ?? 0}/
                                {review.progress.CSM?.owned ?? 0}
                              </p>
                            )}
                            {review && !review.no_answers && (
                              <p className="text-xs text-gray-500">
                                {FLOW.briefingQuestions}:{" "}
                                {review.gaps
                                  ? `${review.gaps} not fully answered`
                                  : "all answered"}
                              </p>
                            )}
                          </Col>
                          <Col title="Timeline">
                            <ul className="space-y-0.5 text-xs text-gray-600">
                              <li>
                                Uploaded {shortDate(f.created_at)}
                                {!compact && f.uploaded_by_name
                                  ? ` · ${f.uploaded_by_name}`
                                  : ""}
                              </li>
                              {/* F-UF-E19 (design §06, §12): the reason the AE gave
                                for using a plan the checks stopped stays on it. */}
                              {f.validation?.override?.reason && (
                                <li data-testid="override-reason">
                                  Used anyway
                                  {!compact && f.validation.override.name
                                    ? ` · ${f.validation.override.name}`
                                    : ""}
                                  : &ldquo;{f.validation.override.reason}&rdquo;
                                </li>
                              )}
                              {/* The AE's note from Confirm plan (2026-10-31). */}
                              {f.confirm_note && (
                                <li data-testid="history-confirm-note">
                                  Confirmed
                                  {f.confirmed_by_name
                                    ? ` · ${f.confirmed_by_name}`
                                    : ""}
                                  : &ldquo;{f.confirm_note}&rdquo;
                                </li>
                              )}
                              {f.published && (
                                <li>Published {shortDate(f.published.at)}</li>
                              )}
                              {info.replacedAt && (
                                <li>Replaced {shortDate(info.replacedAt)}</li>
                              )}
                            </ul>
                          </Col>
                        </div>
                        {!compact && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {onOpenViewer && !f.purged && (
                              <button
                                type="button"
                                onClick={() => onOpenViewer(f.id)}
                                className={SMALL_BTN}
                              >
                                {FLOW.viewSource}
                              </button>
                            )}
                            {live && onOpenPublished && (
                              <button
                                type="button"
                                onClick={onOpenPublished}
                                className={SMALL_BTN}
                              >
                                {FLOW.openPublished}
                              </button>
                            )}
                            {draftable && onOpenDraft && (
                              <button
                                type="button"
                                onClick={onOpenDraft}
                                className={SMALL_BTN}
                              >
                                Open draft briefing
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
};

export default VersionHistory;
